// features/automation/components/RunDetailsPanel.js
 
  import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
  import ReactDOM from 'react-dom';
  import PropTypes from 'prop-types';
  import { CircleCheck, CircleX, MinusCircle, Ban, Layers, Clock, AlertTriangle, Image as ImageIcon, Monitor, Radio, Globe, ChevronDown, ChevronRight, Database, Code2, Loader2, Smartphone } from 'lucide-react';
  import { STEP_TYPE_CONFIG } from '../config/automationConstants';
  import { useStepCache } from '../../../store/automation/runStore';
  import {
    PERMUTATION_STATUS,
    STEP_RESULT,
    getPermutationStatus,
    getStepResult,
    resolveRunCounts,
  } from '../../../shared/utils/permutationStatus';
  import '../styles/RunProgressBar.css';

  const STATUS_BADGE = {
    PASSED:   { className: 'mock-run-details__status-badge--passed',   icon: CircleCheck, label: 'Passed' },
    FAILED:   { className: 'mock-run-details__status-badge--failed',   icon: CircleX,     label: 'Failed' },
    SKIPPED:  { className: 'mock-run-details__status-badge--skipped',  icon: MinusCircle, label: 'Skipped' },
    DISABLED: { className: 'mock-run-details__status-badge--disabled', icon: Ban,         label: 'Disabled' },
  };

  const PENDING_BADGE = { className: 'mock-run-details__status-badge--skipped', icon: MinusCircle, label: 'Pending' };

  // Permutation badge, resolved by status (never by hasPassed — SKIPPED and DISABLED
  // permutations report hasPassed: true).
  const getPermBadge = (perm) => STATUS_BADGE[getPermutationStatus(perm)] || PENDING_BADGE;

  // Step display state. The step's derived `result` (PASS/FAIL/SKIP/DISABLED) drives
  // the badge; endDateTime only distinguishes "still running".
  const getStepState = (step) => {
    if (step.isGroupHeader) return 'header';
    const result = getStepResult(step);
    if (result === STEP_RESULT.PASS)     return 'passed';
    if (result === STEP_RESULT.FAIL)     return 'failed';
    if (result === STEP_RESULT.DISABLED) return 'disabled';
    if (result === STEP_RESULT.SKIP)     return 'skipped';
    return 'in_progress';
  };

  const STEP_BADGE_BY_STATE = {
    passed: STATUS_BADGE.PASSED,
    failed: STATUS_BADGE.FAILED,
    skipped: STATUS_BADGE.SKIPPED,
    disabled: STATUS_BADGE.DISABLED,
  };

  const getStepBadge = (step) => STEP_BADGE_BY_STATE[getStepState(step)] || STATUS_BADGE.SKIPPED;
 
  const getScreenshotUrl = (step) =>
    step.streamScreenshot || step.screenshot || (step.description?.startsWith('data:image') ? step.description : null);
 
  /* ── Merge cached steps with incoming steps ─────────────────────── */
 
  const mergeSteps = (cached, incoming) => {
    if (!cached?.length) return incoming || [];
    if (!incoming?.length) return cached;
 
    const merged = [...cached];
    for (const step of incoming) {
      const existingIdx = merged.findIndex(
        (s) => s.alias === step.alias && s.name === step.name
      );
      if (existingIdx >= 0) {
        // Update existing step with latest data (e.g. new streamScreenshot)
        merged[existingIdx] = step;
      } else {
        merged.push(step);
      }
    }
    return merged;
  };
 
  /* ── Web-UI Steps View ─────────────────────────────────────────── */
 
  const WebUiStepsView = ({ permutations }) => {
    const [selectedStep, setSelectedStep] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const stepsRef = useRef(null);
 
    // permutations already have cached steps baked in by RunDetailsPanel
    const allSteps = permutations.flatMap((p) => p.steps || []);
 
    // Auto-select latest step with a screenshot as it streams in
    const latestScreenshotStep = [...allSteps].reverse().find(s => getScreenshotUrl(s));
 
    // The step whose screenshot to show — user selection takes priority, otherwise latest
    const activeStep = selectedStep && getScreenshotUrl(selectedStep) ? selectedStep : latestScreenshotStep;
    const activeScreenshot = activeStep ? getScreenshotUrl(activeStep) : null;
 
    const handleStepClick = useCallback((step) => {
      setSelectedStep(prev => prev === step ? null : step);
    }, []);
 
    return (
<>
<div className="mock-run-details__stream-layout">
          {/* Left: steps list */}
<div className="mock-run-details__stream-steps" ref={stepsRef}>
            {permutations.flatMap((p, pIdx) => {
              const permLabel = permutations.length > 1 ? (
<div
                  key={`perm-label-${pIdx}`}
                  className="mock-run-details__perm-label"
>
<Layers size={10} />
<span>Permutation {p.index || pIdx + 1}{p.description ? ` — ${p.description}` : ''}</span>
                  {getPermutationStatus(p) && (
<span className={`mock-run-details__status-badge ${getPermBadge(p).className}`}>
                      {React.createElement(getPermBadge(p).icon, { size: 9 })}
                      {getPermBadge(p).label}
</span>
                  )}
</div>
              ) : null;
 
              const stepRows = (p.steps || []).map((step, sIdx) => {
                const state = getStepState(step);
                const badge = getStepBadge(step);
                const BadgeIcon = badge.icon;
                const hasScreenshot = !!getScreenshotUrl(step);
                const isActive = activeStep === step;
                const errors = state === 'failed' && step.assertionResponse?.results?.length
                  ? step.assertionResponse.results
                  : null;

                // Group header — section divider, no timing or status badge
                if (state === 'header') {
                  return (
<div
                      key={`${pIdx}-${sIdx}`}
                      className="mock-run-details__step-row mock-run-details__step-row--header"
>
<div className="mock-run-details__step-left">
<div className="mock-run-details__step-header">
<StepTypeIcon stepType={step.stepType} size={12} />
<span className="mock-run-details__step-name">{step.name}</span>
</div>
</div>
</div>
                  );
                }

                return (
<div
                    key={`${pIdx}-${sIdx}`}
                    className={[
                      'mock-run-details__step-row',
                      state === 'failed' ? 'mock-run-details__step-row--failed' : '',
                      state === 'skipped' ? 'mock-run-details__step-row--skipped' : '',
                      state === 'disabled' ? 'mock-run-details__step-row--disabled' : '',
                      state === 'in_progress' ? 'mock-run-details__step-row--in-progress' : '',
                      isActive ? 'mock-run-details__step-row--active' : '',
                      hasScreenshot ? 'mock-run-details__step-row--clickable' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={hasScreenshot ? () => handleStepClick(step) : undefined}
>
<div className="mock-run-details__step-left">
<div className="mock-run-details__step-header">
                        {state === 'in_progress'
                          ? <Loader2 size={10} className="mock-run-details__step-spinner" />
                          : (
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={10} />
                              {badge.label}
</span>
                          )
                        }
<span className="mock-run-details__step-name">{step.name}</span>
                        {hasScreenshot && (
<span className="mock-run-details__step-has-img">
<ImageIcon size={9} />
</span>
                        )}
</div>
<div className="mock-run-details__step-meta">
                        {step.duration && (
<span className="mock-run-details__duration">{step.duration}</span>
                        )}
                        {step.requestUrl && (
<>
<Globe size={9} />
                            {step.httpMethods && <span className="mock-run-details__step-type">{step.httpMethods}</span>}
<span className="mock-run-details__step-desc">{step.requestUrl}</span>
                            {step.responseStatusCode > 0 && (
<span className={`mock-run-details__inline-step-code ${step.responseStatusCode >= 400 ? 'mock-run-details__inline-step-code--error' : ''}`}>
                                {step.responseStatusCode}
</span>
                            )}
</>
                        )}
</div>
                      {errors && (
<div className="mock-run-details__step-errors">
                          {errors.map((err, errIdx) => (
<span key={errIdx} className="mock-run-details__step-error">
<AlertTriangle size={9} />
                              {err}
</span>
                          ))}
</div>
                      )}
</div>
</div>
                );
              });
 
              return permLabel ? [permLabel, ...stepRows] : stepRows;
            })}
</div>
 
          {/* Right: live screenshot preview */}
<div className="mock-run-details__stream-preview">
            {activeScreenshot ? (
<img
                src={activeScreenshot}
                alt={activeStep?.name || 'Step screenshot'}
                className="mock-run-details__preview-img"
                onClick={() => setIsExpanded(true)}
              />
            ) : (
<div className="mock-run-details__preview-empty">
<Monitor size={28} />
<span>Screenshots will appear here as steps execute</span>
</div>
            )}
            {activeStep && (
<div className="mock-run-details__preview-caption">
<span className="mock-run-details__step-num">{activeStep.alias}</span>
<span>{activeStep.name}</span>
</div>
            )}
            {selectedStep && (
<button
                className="mock-run-details__view-latest-btn"
                onClick={() => setSelectedStep(null)}
>
<Radio size={10} />
                View Latest
</button>
            )}
</div>
</div>
 
        {/* Expanded screenshot overlay — portal to body so it's above everything */}
        {isExpanded && activeScreenshot && ReactDOM.createPortal(
<div
            className="mock-run-details__img-overlay"
            onClick={() => setIsExpanded(false)}
>
<img src={activeScreenshot} alt="Screenshot" className="mock-run-details__img-expanded" />
</div>,
          document.body
        )}
</>
    );
  };
 
  /* ── Step Type Icon Helper ──────────────────────────────────────── */
 
  // Backend streams stepType as uppercase (e.g. "MOBILE", "WEB-UI") — normalise for config lookup
  const normaliseStreamStepType = (t) => {
    if (!t) return t;
    const map = { MOBILE: 'Mobile', 'WEB-UI': 'Web-UI', API: 'Api', DATABASE: 'Database' };
    return map[t.toUpperCase()] ?? t;
  };

  const STEP_TYPE_ICON = { Api: Code2, 'Web-UI': Globe, Database, Mobile: Smartphone };
 
  const StepTypeIcon = ({ stepType, size = 10 }) => {
    const normalised = normaliseStreamStepType(stepType);
    const Icon = STEP_TYPE_ICON[normalised];
    const config = STEP_TYPE_CONFIG?.[normalised];
    if (!Icon) return null;
    return <Icon size={size} style={config ? { color: config.color } : undefined} />;
  };
 
  /* ── Group steps by isGroupHeader ──────────────────────────────── */
 
  const groupStepsByHeader = (steps) => {
    if (!steps?.length) return [];
    const groups = [];
    let currentGroup = null;
 
    for (const step of steps) {
      if (step.isGroupHeader) {
        currentGroup = { header: step, actions: [] };
        groups.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.actions.push(step);
      } else {
        // No header yet — treat as standalone step
        groups.push({ header: null, actions: [step] });
      }
    }
    return groups;
  };
 
  /* ── Helpers ───────────────────────────────────────────────────── */

  const formatBytes = (bytes) => {
    if (bytes == null) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /* ── Inline Step Detail Row ────────────────────────────────────── */

  const StepDetailRow = ({ step }) => {
    const badge = getStepBadge(step);
    const BadgeIcon = badge.icon;
    const normType = normaliseStreamStepType(step.stepType);
    const isDb = normType === 'Database';
    const method = step.httpMethod || step.httpMethods;
    // Only a FAIL result tints the row red — a skipped or disabled step is neither
    // a pass nor a failure (and both report hasPassed: true).
    const state = getStepState(step);

    return (
<div className={[
        'mock-run-details__inline-step',
        state === 'failed' ? 'mock-run-details__inline-step--failed' : '',
        state === 'skipped' || state === 'disabled' ? 'mock-run-details__inline-step--inert' : '',
      ].filter(Boolean).join(' ')}>
        {/* Header: alias + badge + duration */}
<div className="mock-run-details__inline-step-header">
<StepTypeIcon stepType={step.stepType} />
<span className="mock-run-details__inline-step-alias">{step.alias || step.name}</span>
          {step.name && step.alias !== step.name && (
<span className="mock-run-details__inline-step-name">{step.name}</span>
          )}
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={9} />
            {badge.label}
</span>
          {step.duration && (
<span className="mock-run-details__duration">{step.duration}</span>
          )}
</div>

        {/* Method + URL + status code + response size */}
        {step.requestUrl && (
<div className="mock-run-details__inline-step-request">
            {isDb ? <Database size={9} /> : <Globe size={9} />}
            {method && <span className="mock-run-details__inline-step-method">{method}</span>}
<span className="mock-run-details__inline-step-url">{step.requestUrl}</span>
            {step.responseStatusCode > 0 && (
<span className={`mock-run-details__inline-step-code ${step.responseStatusCode >= 400 ? 'mock-run-details__inline-step-code--error' : ''}`}>
                {step.responseStatusCode}
</span>
            )}
            {step.responseSizeBytes != null && (
<span className="mock-run-details__inline-step-size">{formatBytes(step.responseSizeBytes)}</span>
            )}
</div>
        )}

        {/* DB: row + column count */}
        {isDb && (step.rowCount != null || step.columnCount != null) && (
<div className="mock-run-details__step-db-meta">
            {step.rowCount != null && <span>{step.rowCount} row{step.rowCount !== 1 ? 's' : ''}</span>}
            {step.columnCount != null && <span>{step.columnCount} col{step.columnCount !== 1 ? 's' : ''}</span>}
</div>
        )}

        {/* Request body / SQL query preview */}
        {step.requestBody && (
<div className="mock-run-details__body-preview">
<span className="mock-run-details__body-label">{isDb ? 'Query' : 'Request'}</span>
<pre className="mock-run-details__body-content">{step.requestBody}</pre>
            {step.bodyTruncated && (
<span className="mock-run-details__body-truncated">Preview — full body in report</span>
            )}
</div>
        )}

        {/* Response / result preview */}
        {step.responseBody && (
<div className="mock-run-details__body-preview">
<span className="mock-run-details__body-label">{isDb ? 'Result' : 'Response'}</span>
<pre className="mock-run-details__body-content">{step.responseBody}</pre>
            {step.bodyTruncated && (
<span className="mock-run-details__body-truncated">Preview — full body in report</span>
            )}
</div>
        )}

        {/* Extracted variables */}
        {step.extractedData && Object.keys(step.extractedData).length > 0 && (
<div className="mock-run-details__extracted">
            {Object.entries(step.extractedData).map(([k, v]) => (
<span key={k} className="mock-run-details__extracted-item">
<span className="mock-run-details__extracted-key">{k}</span>
<span className="mock-run-details__extracted-val">{String(v)}</span>
</span>
            ))}
</div>
        )}

        {/* Failure reason */}
        {step.failureReason && (
<div className="mock-run-details__step-error">
<AlertTriangle size={9} />
            {step.failureReason}
</div>
        )}

        {/* Assertions — always present, each with individual pass/fail + expected vs actual */}
        {step.assertionResponse?.results?.length > 0 && (
<div className="mock-run-details__step-assertions">
            {step.assertionResponse.results.map((result, i) => {
              const isPassed = typeof result === 'object'
                ? (result.pass ?? result.passed ?? step.assertionResponse.passed)
                : step.assertionResponse.passed;
              const msg = typeof result === 'object'
                ? (result.message || result.description || '')
                : result;
              const expected = typeof result === 'object' ? result.expected : null;
              const actual   = typeof result === 'object' ? result.actual   : null;
              return (
<div key={i} className={`mock-run-details__step-assertion ${isPassed ? 'mock-run-details__step-assertion--passed' : 'mock-run-details__step-assertion--failed'}`}>
<span className="mock-run-details__step-assertion-icon">
                    {isPassed ? <CircleCheck size={9} /> : <AlertTriangle size={9} />}
</span>
<span className="mock-run-details__step-assertion-msg">{msg}</span>
                  {(expected != null || actual != null) && (
<span className="mock-run-details__step-assertion-detail">
                      {expected != null && <span>exp: <code>{String(expected)}</code></span>}
                      {actual   != null && <span>got: <code>{String(actual)}</code></span>}
</span>
                  )}
</div>
              );
            })}
</div>
        )}
</div>
    );
  };
 
  /* ── Expandable Permutation Row ────────────────────────────────── */
 
  const PermutationRow = ({ perm, idx, isExpanded, onToggle }) => {
    const status = getPermutationStatus(perm);
    const badge = getPermBadge(perm);
    const BadgeIcon = badge.icon;
    const stepCount = perm.numberOfSteps || perm.steps?.length || null;
    // Surface the failure reason only for a genuinely FAILED permutation. A skipped
    // or disabled one has no failing step to report, and its steps report
    // hasPassed: true anyway.
    const failedStep = status === PERMUTATION_STATUS.FAILED
      ? perm.steps?.find(s => getStepResult(s) === STEP_RESULT.FAIL)
      : null;
    const failureText = failedStep?.assertionResponse?.results?.[0] || failedStep?.failureReason;
    const hasSteps = perm.steps?.length > 0;
 
    const stepGroups = useMemo(
      () => isExpanded ? groupStepsByHeader(perm.steps) : [],
      [isExpanded, perm.steps]
    );
 
    return (
<>
<tr
          key={`${perm.index}-${idx}`}
          className={`mock-run-details__row ${hasSteps ? 'mock-run-details__row--clickable' : ''}`}
          onClick={hasSteps ? () => onToggle(idx) : undefined}
>
<td>
            {hasSteps && (
              isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />
            )}
</td>
<td>{perm.index}</td>
<td>
<div className="mock-run-details__perm-info">
<span className="mock-run-details__perm-name">
                {perm.description || `Permutation ${perm.index}`}
</span>
              {failureText && (
<span className="mock-run-details__perm-error">
<AlertTriangle size={9} />
                  {failureText}
</span>
              )}
</div>
</td>
<td>
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={11} />
              {badge.label}
</span>
</td>
<td>
            {stepCount != null && (
<span className="mock-run-details__step-count">{stepCount}</span>
            )}
</td>
<td>
<span className="mock-run-details__duration">{perm.duration || '—'}</span>
</td>
</tr>
 
        {/* Expanded step details */}
        {isExpanded && (
<tr className="mock-run-details__expanded-row">
<td colSpan={6}>
<div className="mock-run-details__expanded-steps">
                {stepGroups.length > 0 ? stepGroups.map((group, gIdx) => (
<div key={gIdx} className="mock-run-details__step-group">
                    {group.header && (
<div className="mock-run-details__step-group-header-row">
<StepTypeIcon stepType={group.header.stepType} size={12} />
<span className="mock-run-details__step-group-label">{group.header.alias || group.header.name}</span>
                        {group.header.name && group.header.alias !== group.header.name && (
<span className="mock-run-details__step-group-name">{group.header.name}</span>
                        )}
</div>
                    )}
                    {group.actions.map((step, sIdx) => (
<StepDetailRow key={sIdx} step={step} />
                    ))}
</div>
                )) : (
                  perm.steps?.map((step, sIdx) => (
<StepDetailRow key={sIdx} step={step} />
                  ))
                )}
</div>
</td>
</tr>
        )}
</>
    );
  };
 
  /* ── Permutations Table (Api / Database runs) ──────────────────── */
 
  const PermutationsTable = ({ permutations }) => {
    const [expandedIdx, setExpandedIdx] = useState(null);
 
    const handleToggle = useCallback((idx) => {
      setExpandedIdx(prev => prev === idx ? null : idx);
    }, []);
 
    return (
<table className="mock-run-details__table">
<thead>
<tr>
<th style={{ width: 24 }} />
<th>#</th>
<th>Permutation</th>
<th>Status</th>
<th>Steps</th>
<th>Duration</th>
</tr>
</thead>
<tbody>
          {permutations.map((p, idx) => (
<PermutationRow
              key={`${p.index}-${idx}`}
              perm={p}
              idx={idx}
              isExpanded={expandedIdx === idx}
              onToggle={handleToggle}
            />
          ))}
</tbody>
</table>
    );
  };
 
  const RunDetailsPanel = ({ isOpen, runState }) => {
    const tableWrapRef = useRef(null);
    const userScrolledRef = useRef(false);
    const stepCache = useStepCache();
 
    // Auto-scroll the table wrapper to bottom as new rows arrive
    // Uses scrollTop instead of scrollIntoView to avoid moving the outer page container
    const permCount = runState?.testResults?.permutations?.length || 0;
    useEffect(() => {
      if (!isOpen || userScrolledRef.current) return;
      const wrap = tableWrapRef.current;
      if (wrap) {
        wrap.scrollTop = wrap.scrollHeight;
      }
    }, [isOpen, permCount]);
 
    // Detect user scroll to pause auto-scroll
    useEffect(() => {
      const wrap = tableWrapRef.current;
      if (!wrap) return;
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = wrap;
        userScrolledRef.current = scrollHeight - scrollTop - clientHeight > 60;
      };
      wrap.addEventListener('scroll', handleScroll, { passive: true });
      return () => wrap.removeEventListener('scroll', handleScroll);
    }, [isOpen]);
 
    // Reset scroll tracking when panel opens, and scroll outer container to bottom
    useEffect(() => {
      if (isOpen) {
        userScrolledRef.current = false;
      }
    }, [isOpen]);
 
    if (!runState?.testResults) return null;
 
    const { testSummary, permutations: rawPermutations = [] } = runState.testResults;
    const { runStatus } = testSummary;
    // Counted by status. Note `total` is NOT testSummary.numberOfPermutations — in the
    // stream that field reports how many have executed so far, so it under-reports the
    // grand total (see resolveRunCounts).
    const {
      total: totalPermutations,
      executed: executedPermutations,
      passed: passedPermutations,
      failed: failedPermutations,
      skipped: skippedPermutations,
      disabled: disabledPermutations,
    } = resolveRunCounts(testSummary, rawPermutations);

    // Merge cached steps with incoming steps so completed runs always show
    // the full step list even if the latest WS payload had truncated/no steps.
    const permutations = rawPermutations.map((p, pIdx) => {
      const permKey = p.index != null ? String(p.index) : String(pIdx);
      const cached = stepCache[permKey] || [];
      const incoming = p.steps || [];
      return { ...p, steps: mergeSteps(cached, incoming) };
    });
 
    const isRunning = runStatus === 'IN_PROGRESS' || runStatus === 'RUNNING';
    // Mobile and Web-UI both use the screenshot streaming view
    const hasScreenshotSteps = permutations.some(p =>
      p.steps?.some(s => {
        const t = normaliseStreamStepType(s.stepType);
        return t === 'Web-UI' || t === 'Mobile';
      })
    );
 
    return (
<div className={`mock-run-details${isOpen ? ' mock-run-details--open' : ''}`}>
<div className="mock-run-details__content">
          {/* Header */}
<div className="mock-run-details__header">
<h2 className="mock-run-details__title">Run Details</h2>
<span className="mock-run-details__runid">{runState.runid}</span>
</div>
 
          {/* Meta row — project, type, timestamp */}
          {(runState.projectName || runState.runnableType || runState.timestamp) && (
<div className="mock-run-details__meta">
              {runState.projectName && (
<span className="mock-run-details__meta-tag">
<Layers size={9} />
                  {runState.projectName}
</span>
              )}
              {runState.runnableType && (
<span className="mock-run-details__meta-tag">
                  {runState.runnableType.replace(/_/g, ' ')}
</span>
              )}
              {runState.timestamp && (
<span className="mock-run-details__meta-tag">
<Clock size={9} />
                  {new Date(runState.timestamp).toLocaleTimeString()}
</span>
              )}
</div>
          )}
 
          {/* Permutation stepper — visual pill for each permutation */}
          {totalPermutations > 0 && (
<div className="mock-run-details__perm-stepper">
              {Array.from({ length: totalPermutations }, (_, i) => {
                const perm = permutations[i];
                const permIndex = i + 1;
                const status = getPermutationStatus(perm);
                // Status is the only signal — hasPassed is true for SKIPPED/DISABLED.
                const isCurrent = isRunning && !status && permIndex === executedPermutations + 1;

                let statusClass = 'mock-run-details__perm-pill--pending';
                if (isCurrent) statusClass = 'mock-run-details__perm-pill--running';
                else if (status === PERMUTATION_STATUS.PASSED) statusClass = 'mock-run-details__perm-pill--passed';
                else if (status === PERMUTATION_STATUS.FAILED) statusClass = 'mock-run-details__perm-pill--failed';
                else if (status === PERMUTATION_STATUS.SKIPPED) statusClass = 'mock-run-details__perm-pill--skipped';
                else if (status === PERMUTATION_STATUS.DISABLED) statusClass = 'mock-run-details__perm-pill--disabled';

                return (
<div
                    key={i}
                    className={`mock-run-details__perm-pill ${statusClass}`}
                    title={status ? (STATUS_BADGE[status]?.label || status) : 'Pending'}
>
<span className="mock-run-details__perm-pill-num">{permIndex}</span>
                    {isCurrent && <Loader2 size={10} className="mock-run-details__perm-pill-spinner" />}
                    {!isCurrent && status === PERMUTATION_STATUS.PASSED && <CircleCheck size={10} />}
                    {!isCurrent && status === PERMUTATION_STATUS.FAILED && <CircleX size={10} />}
                    {!isCurrent && status === PERMUTATION_STATUS.SKIPPED && <MinusCircle size={10} />}
                    {!isCurrent && status === PERMUTATION_STATUS.DISABLED && <Ban size={10} />}
</div>
                );
              })}
</div>
          )}

          {/* Summary counts — skipped / disabled shown only when present so a
              straightforward run keeps the compact four-stat row */}
<div className="mock-run-details__summary">
<div className="mock-run-details__stat">
<span className="mock-run-details__stat-label">Total</span>
<span className="mock-run-details__stat-value">{totalPermutations}</span>
</div>
<div className="mock-run-details__stat">
<span className="mock-run-details__stat-label">Executed</span>
<span className="mock-run-details__stat-value">{executedPermutations}</span>
</div>
<div className="mock-run-details__stat mock-run-details__stat--passed">
<span className="mock-run-details__stat-label">Passed</span>
<span className="mock-run-details__stat-value">{passedPermutations}</span>
</div>
<div className="mock-run-details__stat mock-run-details__stat--failed">
<span className="mock-run-details__stat-label">Failed</span>
<span className="mock-run-details__stat-value">{failedPermutations}</span>
</div>
              {skippedPermutations > 0 && (
<div className="mock-run-details__stat mock-run-details__stat--skipped" title="Every step in these permutations was disabled, so nothing ran">
<span className="mock-run-details__stat-label">Skipped</span>
<span className="mock-run-details__stat-value">{skippedPermutations}</span>
</div>
              )}
              {disabledPermutations > 0 && (
<div className="mock-run-details__stat mock-run-details__stat--disabled" title="Turned off in the data source">
<span className="mock-run-details__stat-label">Disabled</span>
<span className="mock-run-details__stat-value">{disabledPermutations}</span>
</div>
              )}
</div>
 
          {/* Results — Web-UI steps view OR permutations table */}
<div className="mock-run-details__table-wrap" ref={tableWrapRef}>
            {permutations.length === 0 ? (
<div className="mock-run-details__empty">
                {isRunning ? 'Waiting for results...' : 'No permutations recorded.'}
</div>
            ) : hasScreenshotSteps ? (
<WebUiStepsView permutations={permutations} />
            ) : (
<PermutationsTable permutations={permutations} />
            )}
</div>
</div>
</div>
    );
  };
 
  RunDetailsPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    runState: PropTypes.object,
  };                                                                                                                                                                                                                                     
  RunDetailsPanel.defaultProps = {                                                                                                                                                                                                       
    runState: null,                                         
  };
 
  export default RunDetailsPanel;